// src/hooks/useVirtualCardsFilters.ts
import queryString from 'query-string'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { VirtualCardsFiltersBody } from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { VirtualCardTypes } from 'src/enums'
import { useQueryParamArrayHandler } from 'src/hooks'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'

export const useVirtualCardsFilters = (): VirtualCardsFiltersBody => {
  const queryParams = useSelector(selectQueryParams)
  const [urlParams, setUrlParams] = useState(queryParams)

  useEffect(() => {
    const params = queryString.parse(window.location.search)
    setUrlParams(params)
  }, [window.location.search])

  const calculatedStatusFilter = (statuses: string[]): string[] => {
    const statusFilter = []

    if (statuses?.some((status) => status === VirtualCardStatus.Active)) {
      statusFilter.push('active')
    }
    if (statuses?.some((status) => status === VirtualCardStatus.Pending)) {
      statusFilter.push('pending')
    }
    if (
      statuses?.some((status) => status === VirtualCardStatus.Cancelled) &&
      statuses?.some((status) => status === VirtualCardStatus.Expired) &&
      statuses?.some((status) => status === VirtualCardStatus.Rejected)
    ) {
      statusFilter.push('inactive')
    }
    if (
      statuses?.some((status) => status === VirtualCardStatus.Closed) &&
      statuses?.some((status) => status === VirtualCardStatus.Consumed)
    ) {
      statusFilter.push('closed')
    }
    return statusFilter
  }

  const calculatedStatuses = (
    statusesFilter: Set<string>,
  ): VirtualCardStatus[] => {
    const filteredStatuses = []
    if (statusesFilter.size > 0) {
      if (
        statusesFilter.has('active') ||
        statusesFilter.has(VirtualCardStatus.Active)
      ) {
        filteredStatuses.push(VirtualCardStatus.Active)
      }
      if (
        statusesFilter.has('pending') ||
        statusesFilter.has(VirtualCardStatus.Pending)
      ) {
        filteredStatuses.push(VirtualCardStatus.Pending)
      }
      if (
        statusesFilter.has('inactive') ||
        statusesFilter.has(VirtualCardStatus.Cancelled) ||
        statusesFilter.has(VirtualCardStatus.Expired) ||
        statusesFilter.has(VirtualCardStatus.Rejected)
      ) {
        filteredStatuses.push(
          VirtualCardStatus.Cancelled,
          VirtualCardStatus.Expired,
          VirtualCardStatus.Rejected,
        )
      }
      if (
        statusesFilter.has('closed') ||
        statusesFilter.has(VirtualCardStatus.Closed) ||
        statusesFilter.has(VirtualCardStatus.Consumed)
      ) {
        filteredStatuses.push(
          VirtualCardStatus.Closed,
          VirtualCardStatus.Consumed,
        )
      }
    }
    return filteredStatuses
  }

  return useMemo(() => {
    const statusesFilter = new Set(
      calculatedStatusFilter(
        useQueryParamArrayHandler(urlParams.statuses) as string[],
      ),
    )
    const recipientFilter = new Set<string>(
      useQueryParamArrayHandler(urlParams.recipients),
    )
    const cardTypesFilter = new Set(
      useQueryParamArrayHandler(urlParams.cardTypes),
    )

    const filtersBody: VirtualCardsFiltersBody = {
      search: urlParams.search ? urlParams.search : undefined,
      statuses:
        statusesFilter.size > 0
          ? Array.from(calculatedStatuses(statusesFilter))
          : undefined,
      recipients:
        recipientFilter.size > 0 ? Array.from(recipientFilter) : undefined,
      cardTypes:
        cardTypesFilter.size > 0
          ? (Array.from(cardTypesFilter) as VirtualCardTypes[])
          : undefined,
      deactivatingIn: urlParams.deactivatingIn
        ? `${urlParams.deactivatingIn}`
        : undefined,
      sortField: 'updatedAt',
      sortDirection: 'DESC',
    }

    return filtersBody
  }, [urlParams])
}
